import React, { useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../../../../../../../context/AuthContext";
import { useAutoImportFormContext } from "../../Context/AutoImportFormContext";
import styles from "../AddEditAutoImportConfigurationsModal.module.css";
import { LRequiredInput } from "./CommonComponents/LRequiredInput";
import { FormGrid } from "./CommonComponents/FormGrid";
import { ProductType } from "@appTypes/productType";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";
import { LFormInput } from "@loffa/gpp-modules";
import { Client as FormattedClient } from "@appTypes/clientTypes";
import { ClientProductConfig } from "../types/autoImportConfigContextTypes";
import { useClientsConfig } from "../../../hooks/useClientsConfig";
import { AUTO_IMPORT_PRODUCTS } from "../../../constants/validAutoImportProducts";
import {
  SCHEDULED_RUN_TIMES_DATA,
  SCHEDULED_RUN_TIMES_ENUM,
} from "../../../constants/scheduledRunTimesTypes";

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

export function GeneralInfoForm() {
  const {
    formData,
    setError,
    updateField,
    updateFormData,
    generalInfoDropdownOptions,
    updateGeneralInfoDropdownOptions,
  } = useAutoImportFormContext();
  const authContext = useContext(AuthContext);
  const token = authContext.authState().accessToken.accessToken;
  const {
    clients,
    loading: loadingClients,
    error: clientsError,
  } = useClientsConfig(token);

  const pbinProductIds = AUTO_IMPORT_PRODUCTS.map((e) =>
    e.productTypeGuid.toLowerCase()
  );

  const {
    isActive,
    client,
    jobName,
    scheduleType,
    scheduledTime,
    forDtc,
    associatedInst,
    productType,
  } = formData;

  // Fetch clients for "Client combobox"
  useEffect(() => {
    if (clientsError) {
      setError("Error fetching client list: " + clientsError);
      return;
    }
    updateGeneralInfoDropdownOptions({ activeClientList: clients });
    setError(null);
  }, [clients, clientsError, setError, updateGeneralInfoDropdownOptions]);

  // When a client is picked
  const onClientSelect = (e: KendoComboBoxEvent) => {
    const sel = e.target.value as FormattedClient;
    const dtc = sel?.clientValuePayload.dtcNumber || "";
    updateFormData({
      client: sel,
      productType: undefined,
      clientProductGuid: "",
      forDtc: dtc,
    });
    updateGeneralInfoDropdownOptions({ clientProducts: [] });
    fetchClientProducts(sel);
  };

  // When a product is picked
  const onProductSelect = (e: KendoComboBoxEvent) => {
    const prod = e.target.value as ProductType;
    updateField("productType", prod);
    fetchClientProductConfig(prod);
  };

  const selectedScheduleType = useMemo(
    () => SCHEDULED_RUN_TIMES_DATA.find((item) => item?.value === scheduleType),
    [scheduleType]
  );

  // fetch products for that client
  const fetchClientProducts = async (selClient: FormattedClient) => {
    if (!selClient) return;
    try {
      const res = await fetch(
        `${GATEWAY_BASE_URL}/multitenancy/api/Clients/${selClient.clientValuePayload.clientGuid}/Products`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch products.");
      const list: ProductType[] = await res.json();
      const pbin = list.filter((p) =>
        pbinProductIds.includes(p.productTypeId.toLowerCase())
      );
      updateGeneralInfoDropdownOptions({ clientProducts: pbin });
      setError(null);
    } catch (err: any) {
      setError("Error fetching products: " + err.message);
    }
  };

  // fetch the product-guid after both client + product
  const fetchClientProductConfig = async (prod: ProductType) => {
    if (!client || !prod) return;
    try {
      const res = await fetch(
        `${GATEWAY_BASE_URL}/multitenancy/api/Clients/${client.clientValuePayload.clientGuid}/Products/${prod.productGuid}/Configuration`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch config.");
      const result: ClientProductConfig = await res.json();
      updateField("clientProductGuid", result.clientProduct.clientProductGuid);
      setError(null);
    } catch (err: any) {
      setError("Error fetching product config: " + err.message);
    }
  };

  const inputs = useMemo(
    () => [
      {
        colSpan: 12,
        render: () => (
          <div className={styles.checkboxInputAlign}>
            <LFormInput
              type="checkbox"
              labelName="Is Active"
              checkbox={{
                value: isActive,
                checked: isActive,
                onChange: (e: any) => updateField("isActive", e.value),
              }}
            />
          </div>
        ),
      },
      {
        colSpan: 6,
        render: () => (
          <LRequiredInput
            type="input"
            labelAlign="top"
            required
            labelName="Job Name"
            input={{
              name: "Job Name",
              placeholder: "Enter Job Name",
              value: jobName || "",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                updateField("jobName", e.target.value),
            }}
          />
        ),
      },
      {
        colSpan: 6,
        render: () => (
          <div className={styles.comboBoxLabelAlign}>
            <LRequiredInput
              type="combobox"
              labelAlign="top"
              required
              labelName="Scheduled Time"
              comboBox={{
                data: SCHEDULED_RUN_TIMES_DATA,
                textField: "text",
                dataItemKey: "value",
                value: selectedScheduleType,
                onChange: (e: KendoComboBoxEvent) => {
                  const { value: target } = e.target;
                  updateField("scheduleType", target?.value);
                },
                placeholder: "-- Choose a Scheduled Time --",
              }}
            />
          </div>
        ),
      },
      ...(selectedScheduleType?.value === SCHEDULED_RUN_TIMES_ENUM.DAILY
        ? [
            {
              colSpan: 12,
              render: () => (
                <>
                  <div className={styles.scheduledTimePickerLabel}>
                    Scheduled Time
                  </div>
                  <input
                    type="time"
                    step={1}
                    className={styles.scheduledTimePicker}
                    value={scheduledTime}
                    onChange={(e) =>
                      updateField("scheduledTime", e.target.value)
                    }
                    pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                  />
                </>
              ),
            },
          ]
        : []),
      {
        colSpan: 6,
        render: () => (
          <div className={styles.comboBoxLabelAlign}>
            <LRequiredInput
              type="combobox"
              labelAlign="top"
              required
              labelName="Client"
              comboBox={{
                data: generalInfoDropdownOptions.activeClientList,
                textField: "formattedName",
                valueField: "clientValuePayload",
                dataItemKey: "clientValuePayload.clientGuid",
                value: client || null,
                onChange: onClientSelect,
                placeholder: loadingClients
                  ? "Loading clients..."
                  : "-- Choose a Client --",
                disabled: loadingClients,
              }}
            />
          </div>
        ),
      },
      {
        colSpan: 3,
        render: () => (
          <LRequiredInput
            type="input"
            labelAlign="top"
            required
            labelName="To DTC"
            input={{
              name: "To DTC",
              placeholder: "Enter Client DTC#",
              value: forDtc,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                updateField("forDtc", e.target.value),
            }}
          />
        ),
      },
      {
        colSpan: 3,
        render: () => (
          <LRequiredInput
            type="input"
            labelAlign="top"
            required
            labelName="Inst. Number"
            input={{
              name: "Inst. Number",
              placeholder: "Enter Inst. Number",
              value: associatedInst,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                updateField("associatedInst", e.target.value),
            }}
          />
        ),
      },
      {
        colSpan: 12,
        render: () => (
          <div className={styles.comboBoxLabelAlign}>
            <LRequiredInput
              type="combobox"
              labelName="Client Product"
              labelAlign="top"
              required
              comboBox={{
                data: generalInfoDropdownOptions.clientProducts,
                textField: "name",
                dataItemKey: "productGuid",
                value: productType || null,
                onChange: onProductSelect,
                placeholder: "-- Choose Client Product --",
                disabled: !client,
              }}
            />
          </div>
        ),
      },
    ],
    [
      isActive,
      jobName,
      scheduleType,
      scheduledTime,
      forDtc,
      associatedInst,
      productType,
      generalInfoDropdownOptions.activeClientList,
      client,
      generalInfoDropdownOptions.clientProducts,
      loadingClients,
    ]
  );

  return <FormGrid items={inputs} />;
}
